import { Lineicons, type LineiconsProps } from '@lineiconshq/react-lineicons';
import { RefreshCircle1ClockwiseStroke } from '@lineiconshq/free-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useExcelOperationLoadingSignal } from '@/lib/excel/operation-events';
import { cn } from '@/lib/utils';

export interface AritoActionButtonProps {
  title?: string;
  icon: LineiconsProps['icon'];
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'destructive';
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  disabled?: boolean;
  shortcut?: string;
  loading?: boolean;
  loadingTitle?: string;
}

export const AritoActionButton: React.FC<AritoActionButtonProps> = ({
  title,
  icon,
  onClick,
  variant = 'secondary',
  type = 'button',
  form,
  disabled = false,
  shortcut,
  loading = false,
  loadingTitle
}) => {
  const resolvedTitle = loading && loadingTitle ? loadingTitle : title;
  const normalizedActionTitle = title?.trim().toLocaleLowerCase('vi-VN');
  const tooltipTitle = shortcut ? `${resolvedTitle} (${shortcut})` : resolvedTitle;
  const isDisabled = disabled || loading;
  const shouldShowExcelOverlay = loading && /excel|xuất|kết xuất/i.test(`${title || ''} ${loadingTitle || ''}`);

  useExcelOperationLoadingSignal(
    shouldShowExcelOverlay,
    loadingTitle || 'Đang kết xuất dữ liệu...',
    `action-button-${title || 'excel'}`
  );

  const getButtonColorClass = () => {
    if (isDisabled) return 'cursor-not-allowed bg-gray-800 opacity-50 text-white';

    const normalizedTitle = title?.trim().toLowerCase();

    if (
      normalizedTitle === 'xóa' ||
      normalizedTitle === 'xoá' ||
      normalizedTitle === 'đóng' ||
      normalizedTitle === 'hủy' ||
      normalizedTitle === 'huỷ' ||
      normalizedTitle === 'close' ||
      normalizedTitle === 'cancel'
    ) {
      return 'bg-red-600 hover:bg-red-700 text-white';
    }

    if (
      normalizedTitle === 'thêm' ||
      normalizedTitle === 'sửa' ||
      normalizedTitle === 'sao chép' ||
      normalizedTitle === 'lưu'
    ) {
      return 'bg-main hover:bg-main-dark text-white';
    }

    if (
      normalizedTitle === 'in ấn' ||
      normalizedTitle === 'tìm kiếm' ||
      normalizedTitle === 'in' ||
      normalizedTitle === 'gửi xử lý' ||
      normalizedTitle === 'refresh' ||
      normalizedTitle === 'cố định cột' ||
      normalizedTitle === 'xuất excel' ||
      normalizedTitle === 'nhập excel' ||
      normalizedTitle === 'import' ||
      normalizedTitle === 'export'
    ) {
      return 'bg-main-light hover:bg-main text-white';
    }

    if (variant === 'primary') return 'bg-main hover:bg-main-dark text-white';
    if (variant === 'destructive') return 'bg-red-800 hover:bg-red-900 text-white';
    return 'bg-main hover:bg-main-dark text-white';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            type={type}
            form={form}
            disabled={isDisabled}
            data-erp-action-title={normalizedActionTitle}
            className={cn(
              'flex h-[var(--erp-command-button-height)] min-w-[70px] flex-col items-center justify-center rounded-[2.5px] px-3 py-0 font-semibold transition duration-75 hover:shadow-sm',
              getButtonColorClass()
            )}
          >
            <Lineicons
              aria-hidden='true'
              icon={loading ? RefreshCircle1ClockwiseStroke : icon}
              className={loading ? 'animate-spin' : undefined}
              size={22}
              strokeWidth={1.8}
            />
            {resolvedTitle && <span className='text-xs'>{resolvedTitle}</span>}
          </button>
        </TooltipTrigger>
        {tooltipTitle && <TooltipContent sideOffset={-10}>{tooltipTitle}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};
